import React, { useMemo } from "react";

const MAX_PROSE_SENTENCES = 2;

type SegmenterInstance = {
  segment: (input: string) => Iterable<{ segment: string }>;
};

/**
 * Splits copy into sentences for bullet rendering. Prefers Intl.Segmenter when available.
 */
export function splitIntoSentences(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  try {
    const IntlWithSeg = Intl as typeof Intl & {
      Segmenter?: new (locale: string, options: { granularity: "sentence" }) => SegmenterInstance;
    };
    const SegmenterCtor = IntlWithSeg.Segmenter;
    if (typeof SegmenterCtor === "function") {
      const segmenter: SegmenterInstance = new SegmenterCtor("en", {
        granularity: "sentence",
      });
      const parts: string[] = [];
      for (const { segment } of Array.from(segmenter.segment(trimmed))) {
        const s = segment.trim();
        if (s.length > 0) parts.push(s);
      }
      if (parts.length > 0) return parts;
    }
  } catch {
    /* use fallback */
  }

  return trimmed
    .split(/(?<=[.!?]["']?)\s+(?=[A-Z0-9(])/g)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

type LiqIntelProseOrBulletsProps = {
  text: string;
  /** Applied to the single &lt;p&gt; when prose, or merged onto the &lt;ul&gt; when bulleted. */
  className?: string;
};

/**
 * Renders up to {@link MAX_PROSE_SENTENCES} sentences as one paragraph; longer text as one bullet per sentence.
 */
export function LiqIntelProseOrBullets({ text, className }: LiqIntelProseOrBulletsProps) {
  const sentences = useMemo(() => splitIntoSentences(text), [text]);
  if (sentences.length === 0) return null;
  if (sentences.length <= MAX_PROSE_SENTENCES) {
    return <p className={className}>{text.trim()}</p>;
  }
  const listClass = ["liq-sentence-bullets", className].filter(Boolean).join(" ");
  return (
    <ul className={listClass}>
      {sentences.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ul>
  );
}
